import { ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useWindowSizeStore } from '@/stores/useWindowSizeStore';
import { useSidebarLayoutStore } from '@/stores/useSidebarLayoutStore';
import { useSidebarIsOpen } from '@/composables/useSidebarIsOpen';
import {LEFT_SIDEBAR, RIGHT_SIDEBAR} from "@/types/ui";

export function useThreeColumnLayoutLayout() {
  const leftDefaultOpen = true, rightDefaultOpen = false;

  // Store initialization
  const windowSizeStore = useWindowSizeStore();
  const windowSize = storeToRefs(windowSizeStore);

  const sidebarLayoutStore = useSidebarLayoutStore();
  const sidebarLayout = storeToRefs(sidebarLayoutStore);

  // Sidebar states
  const isRightOpen = useSidebarIsOpen(RIGHT_SIDEBAR, rightDefaultOpen);
  const isLeftOpen = useSidebarIsOpen(LEFT_SIDEBAR, leftDefaultOpen);

  // Panel sizes
  const leftSize = ref(0);
  const rightSize = ref(0);
  const centerSize = ref(100);

  // Calculate the sizes of panels based on sidebar states and window size
  const calculateColumnsSize = () => {
    if (windowSize.width.value < sidebarLayoutStore.screenSmSize) {
      leftSize.value = isLeftOpen.value ? 100 : 0;
      rightSize.value = isRightOpen.value ? 100 : 0;
    } else {
      leftSize.value = isLeftOpen.value ? sidebarLayout.leftSidebarWidth.value : 0;
      rightSize.value = isRightOpen.value ? sidebarLayout.rightSidebarWidth.value : 0;
    }
    centerSize.value = 100 - (leftSize.value + rightSize.value);
  };

  // Handler for resizing sidebars (splitter may call with undefined/incomplete params on nav or click)
  const handleResizeSidebars = (params: any) => {
    const prev = params?.prevPane;
    if (prev == null || typeof prev.index !== 'number') return;
    const roundSize = (size: number) => ~~size;
    if (prev.index === 0) {
      sidebarLayoutStore.updateLeftSidebarWidth(roundSize(prev.size));
    } else if (prev.index === 1) {
      const next = params?.nextPane;
      if (next != null) sidebarLayoutStore.updateRightSidebarWidth(roundSize(next.size));
    }
  };

  // Setup watchers
  watch(isLeftOpen, () => {
    calculateColumnsSize();
  });

  watch(isRightOpen, () => {
    calculateColumnsSize();
  });

  watch(sidebarLayout.leftSidebarWidth, () => {
    calculateColumnsSize();
  });

  watch(sidebarLayout.rightSidebarWidth, () => {
    calculateColumnsSize();
  });

  watch(windowSize.width, () => {
    calculateColumnsSize();
  });

  // Initial calculation
  calculateColumnsSize();

  return {
    isLeftOpen,
    isRightOpen,
    leftSize,
    rightSize,
    centerSize,
    handleResizeSidebars,
    calculateColumnsSize
  };
}
